import { useMutation, useQuery } from "@apollo/client/react";

import FlashContext from "components/FlashMessages/FlashContext";
import ListContentWrapper from "components/ListContentWrapper";
import PageLoading from "components/loading/PageLoading";
import Button from "ds/components/Button";
import PageInfoHeader from "ds/components/PageInfoHeader";
import TableSimple from "ds/components/TableSimple";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { Session } from "types/generated";
import { AccountContext } from "views/AccountWrapper";

import { showSessionsDeactivateAllConfirmation } from "./DeactivateAllConfirmation";
import { GET_SESSIONS, SESSION_DELETE_ALL } from "./gql";
import { SESSIONS_TABLE_CELLS } from "./Table/Cells";
import useSessionsTableStore from "./Table/useTableStore";

const Sessions = () => {
  const { accountName } = useTypedContext(AccountContext);
  const { reportSuccess, onError } = useTypedContext(FlashContext);

  useTitle(`Organization Settings · Sessions · ${accountName}`);

  const { data, loading, error } = useQuery<{ sessions: Session[] }>(GET_SESSIONS);
  const [sessionDeleteAll, { loading: sessionDeleteAllLoading }] = useMutation<{
    deletedSessions: Session[];
  }>(SESSION_DELETE_ALL, { refetchQueries: ["GetSessions"] });

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  const handleDeactivation = () => {
    const asyncFn = async () => {
      try {
        await sessionDeleteAll();

        reportSuccess({ message: `Sessions were deactivated` });
      } catch (error) {
        onError(error);
      }
    };

    void asyncFn();
  };

  const onModalShow = () => {
    showSessionsDeactivateAllConfirmation({ onConfirm: handleDeactivation });
  };

  return (
    <>
      <PageInfoHeader title="Sessions">
        <Button variant="dangerSecondary" loading={sessionDeleteAllLoading} onPress={onModalShow}>
          Deactivate all sessions
        </Button>
      </PageInfoHeader>

      {loading && <PageLoading />}

      {data && (
        <ListContentWrapper>
          <TableSimple
            data={data.sessions}
            store={useSessionsTableStore}
            ariaLabel="Sessions table"
            cellPerColumn={SESSIONS_TABLE_CELLS}
            hideSettings
            hasPagination={false}
          />
        </ListContentWrapper>
      )}
    </>
  );
};

export default Sessions;
